// src/components/ErrorBoundary.js
// React class component — catches render-time errors that hooks cannot catch.
// Renders a dark-themed fallback UI and logs full error details to console + Firestore.
import React from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const message        = error?.message || 'Unknown render error';
    const stack          = error?.stack   || '';
    const componentStack = info?.componentStack || '';

    console.error('[WasteGame] React render error:', message);
    console.error(stack);
    console.error('Component stack:', componentStack);

    // Fire-and-forget Firestore log — swallow any logging failure
    addDoc(collection(db, 'errorLogs'), {
      type:           'react_render_error',
      message,
      stack,
      componentStack,
      userId:         auth.currentUser?.uid || null,
      url:            window.location.href,
      userAgent:      navigator.userAgent,
      timestamp:      serverTimestamp(),
    }).catch(() => {});
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight:      '60vh',
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        '32px 20px',
        textAlign:      'center',
        background:     'var(--dark, #1e2530)',
        color:          'var(--off, #c8d4df)',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{
          fontFamily:    "'Bebas Neue', sans-serif",
          fontSize:      '1.6rem',
          color:         'var(--white, #e0e6ed)',
          marginBottom:  '10px',
          letterSpacing: '1px',
        }}>
          SOMETHING WENT WRONG
        </h2>
        <p style={{
          fontSize:     '0.9rem',
          color:        'var(--muted, #7a8fa0)',
          marginBottom: '24px',
          maxWidth:     '320px',
          lineHeight:   '1.5',
        }}>
          An unexpected error occurred. Please refresh the page — if it keeps happening,
          contact us at hello@thewastegame.co.uk
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background:   'var(--teal, #3ecfa0)',
            color:        '#0d1117',
            border:       'none',
            borderRadius: '8px',
            padding:      '12px 28px',
            fontSize:     '0.9rem',
            fontWeight:   '700',
            cursor:       'pointer',
            letterSpacing:'0.5px',
          }}
        >
          🔄 Refresh Page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
